import React, {memo, useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import type L from 'leaflet';
import cn from 'classnames';
import config from '@config.client';
import {useLocalizer} from '@/common/services/localizer';
import {useErrorInformerPush} from '@/common/services/ErrorInformer';
import {debounce} from '@/common/helpers/utils/debounce';
import {useActualRef} from '@/common/helpers/hooks/useActualRef';
import {MapParams} from '@/app/interfaces/map';
import {selectTemperatureUnit} from '@/app/store/config/selectors';
import {useMapBorders} from './useMapBorders';
import {useMapPoints} from './useMapPoints';
import styles from './WeatherMap.css';

interface WeatherMapProps {
	className?: string;
	params: MapParams;
	hideZoomControls?: boolean;
}

export const WeatherMap = memo<WeatherMapProps>(props => {
	const {className, hideZoomControls} = props;
	const {viewport, bordersGeoJsonId} = props.params;

	const containerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<L.Map | null>(null);

	const temperatureUnit = useSelector(selectTemperatureUnit);

	const errorInformerPush = useErrorInformerPush();
	const {language} = useLocalizer();
	const mapBorders = useMapBorders();
	const mapPoints = useMapPoints({temperatureUnit});

	const setMarkersActual = useActualRef(mapPoints.setMarkers);

	// Debounce error emmit because leaflet emits error on each tail load error
	const tailLoadErrorHandler = useMemo(() => {
		return debounce<[L.TileErrorEvent]>(event => {
			errorInformerPush(event.error);
		}, 500);
	}, []);

	const initializeMap = (leaflet: typeof L) => {
		// Initialize a map
		const map = leaflet.map(containerRef.current!, {
			maxBounds: [
				[-90, -Infinity],
				[90, Infinity],
			],
			maxBoundsViscosity: 1,
		});

		mapRef.current = map;

		// Configure the map view
		map.setView(viewport.center, viewport.zoom);
		if (hideZoomControls) {
			map.zoomControl.remove();
		} else {
			map.zoomControl.setPosition('bottomright');
		}

		leaflet
			.tileLayer(config.map.tileServerUrl, {
				minZoom: 2,
				maxZoom: 13,
				attribution:
					'Map data © <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, ' +
					'<a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>',
			})
			.on('tileerror', tailLoadErrorHandler)
			.addTo(map);

		// Initialize delegate feature hooks
		mapBorders.initialize(leaflet, map);
		mapPoints.initialize(leaflet, map);

		// Initialize borders GEO JSON data loading
		mapBorders.setBorders(bordersGeoJsonId);

		// Initialize Weather points loading
		mapPoints.setMarkers();

		// Listen the map view change events to update weather points

		// The zoom event is debounced because it's triggers on each zoom level change step
		map.on(
			'zoomend',
			debounce(() => setMarkersActual.current(), 600),
		);

		// Intercept a zoom event to ignore the subsequent move end event to process only the moving (pan)
		let ignoreMoveEvent = false;
		map.on('zoomend', () => {
			ignoreMoveEvent = true;
		});

		// Handle move (pan only) event
		map.on('moveend', () => {
			if (ignoreMoveEvent) {
				ignoreMoveEvent = false;
			} else {
				setMarkersActual.current();
			}
		});

		// Clean weather points on zoom out to avoid point markers overlapping
		map.on('zoomanim', e => {
			if (e.zoom < map.getZoom()) {
				mapPoints.resetMarkers();
			}
		});
	};

	// Handle external change of viewport (location changed)
	useEffect(() => {
		if (!mapRef.current) {
			return;
		}

		mapRef.current.setView(viewport.center, viewport.zoom, {animate: true});
		mapBorders.setBorders(bordersGeoJsonId);
	}, [viewport]);

	// Re fetch points on temperature unit changes
	useEffect(() => {
		if (mapRef.current) {
			mapPoints.resetMarkers();
			mapPoints.setMarkers();
		}
	}, [temperatureUnit]);

	// Reset markers when changing language (new ones will be shown after fetch a new points list)
	useEffect(() => {
		if (mapRef.current) {
			mapPoints.resetMarkers();
		}
	}, [language]);

	// Re fetch points to actualize weather points
	useEffect(() => {
		const update = () => {
			const lastUpdatedAt = mapPoints.getMarkersUpdateTime();
			if (lastUpdatedAt && Date.now() - lastUpdatedAt > config.mapWeatherRefreshInterval) {
				mapPoints.setMarkers();
			}
		};

		const checkVisibility = () => {
			if (document.visibilityState === 'visible') {
				update();
			}
		};

		window.addEventListener('focus', update);
		document.addEventListener('visibilitychange', checkVisibility);

		return () => {
			window.removeEventListener('focus', update);
			document.removeEventListener('visibilitychange', checkVisibility);
		};
	}, []);

	// Initialize the map
	useEffect(() => {
		// Load the Leaflet script/style chunks
		/* eslint-disable capitalized-comments, lines-around-comment */
		Promise.all([
			import(/* webpackChunkName: "leaflet", webpackPrefetch: true */ 'leaflet'),
			import(/* webpackChunkName: "leaflet", webpackPrefetch: true */ 'leaflet/dist/leaflet.css'),
		]).then(([leaflet]) => initializeMap(leaflet));
		/* eslint-enable capitalized-comments, lines-around-comment */

		return () => {
			if (mapRef.current) {
				mapRef.current.remove();
			}
		};
	}, []);

	return <div ref={containerRef} className={cn(styles.container, className)} />;
});

WeatherMap.displayName = 'WeatherMap';
